/**
 * Category icons — an emoji drawn on the category's colour disc.
 *
 * The icon is optional data (`Category.icon`, DATA_MODEL §2). It can be absent
 * for three ordinary reasons: a row written before icons existed, a category a
 * user never gave one, and — the one that shapes this module — **an older
 * cached client that strips fields it does not know** and pushes the stripped
 * row on its next edit. So nothing ever renders `icon` directly: it goes
 * through {@link categoryIcon}, which falls back to the seeded icon for a
 * seeded name, then to a neutral tag. No row is backfilled to get there, so no
 * burst of sync writes either.
 *
 * Pure — no Dexie, no DOM.
 */

/** The eight seeded categories' icons (SPEC.md §4), by their seeded name. */
export const DEFAULT_ICON_BY_NAME: Readonly<Record<string, string>> = {
  "Food & Drink": "🍜",
  Groceries: "🛒",
  Transport: "🚌",
  Bills: "💡",
  Shopping: "🛍️",
  Fun: "🎟️",
  Health: "💊",
  // Not an emoji, deliberately: the fallback category is "everything else",
  // and an ellipsis says that without implying a kind of spending.
  Other: "•••",
};

/** For a category with no icon and no seeded name. */
export const FALLBACK_ICON = "🏷️";

/** The icon to draw for a category — never blank. */
export function categoryIcon(category: { name: string; icon?: string | null }): string {
  if (category.icon != null && category.icon !== "") return category.icon;
  return DEFAULT_ICON_BY_NAME[category.name] ?? FALLBACK_ICON;
}

const graphemes = new Intl.Segmenter(undefined, { granularity: "grapheme" });
/** A pictographic code point, a regional-indicator letter (flags), or a keycap. */
const EMOJI = /\p{Extended_Pictographic}|\p{Regional_Indicator}|⃣/u;

/**
 * Whether `text` is exactly one emoji — the only thing a user may set as an
 * icon. One *grapheme*, not one code point: 👩‍👩‍👧 is five code points and one
 * glyph, 🇸🇬 is two, and a code-point count would refuse both.
 */
export function isSingleEmoji(text: string): boolean {
  const segments = [...graphemes.segment(text)];
  if (segments.length !== 1) return false;
  return EMOJI.test(text);
}
